import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, ShieldCheck, Lock, BookOpen } from "lucide-react";
import { ASSETS } from "@/lib/api";
import { PageHead, Wrap, H2, P, Prose, Impact, Callout, DontList, CheckList, Disclaimer } from "@/components/Content";
import { Seo, serviceSchema, breadcrumbSchema } from "@/components/Seo";

const BOOKS = [
  {
    img: ASSETS.comicAvengers4,
    title: "Avengers #4",
    meta: "Marvel Comics · 1964 · CGC 9.8",
    note: "1st Silver Age appearance of Captain America. A benchmark for what high-grade Silver Age presentation can look like.",
    tag: "RESTORATION KIT WAS PART OF",
    pos: "center 62%",
    cropLabel: true,
  },
  {
    img: ASSETS.comicAction88,
    title: "Action Comics #88",
    meta: "DC Comics · 1945 · CGC 9.6 · Pedigree",
    note: "A Golden Age book from a renowned pedigree collection — a study in original paper, color and preservation.",
    tag: "COMICS THAT TAUGHT THE PROCESS",
    pos: "center 20%",
  },
  {
    img: ASSETS.comicCaptainAmerica1,
    title: "Captain America Comics #1",
    meta: "Timely Comics · 1941 · CGC 9.0",
    note: "One of the most historically significant books in the hobby — the kind of comic that teaches restraint and respect for age.",
    tag: "HISTORIC BOOKS. MODERN SCIENCE.",
    pos: "center 22%",
  },
];

const EYE_POINTS = [
  "A trained eye.",
  "An understanding of paper.",
  "An understanding of age.",
  "An understanding of presentation.",
  "And an understanding of when a book may benefit from careful rehabilitation — and when it should simply be left alone.",
];

const REHAB = [
  "Improve overall presentation",
  "Reduce appropriate surface contamination",
  "Minimize the appearance of certain waves or light creases",
  "Improve lay and presentation where appropriate",
  "Address some removable surface grime",
  "Prepare the book for long-term preservation",
  "Replace worn bags and boards with appropriate archival materials",
  "Prepare an eligible comic for grading submission",
];

const WONT = [
  "No recoloring",
  "No trimming",
  "No rebuilding missing paper",
  "No hiding restoration",
  "No aggressive chemical treatments",
  "No direct heat applied to comics",
  "No forcing fragile paper",
  "No guaranteed grade increases",
  "No pretending permanent damage disappeared",
];

const Label = ({ children }) => (
  <p className="eyebrow text-[color:var(--cyan)] mb-3">{children}</p>
);

export default function ComicServices() {
  return (
    <div className="bg-ink min-h-screen pb-24">
      <Seo
        title="Comic Science — Comic Cleaning, Rehabilitation & Preservation | CardScience HQ"
        description="Decades of collectible experience and a trained eye for books worth saving. Museum-grade comic rehabilitation and preservation at CardScience HQ — helping an appropriate book present as the best version of what it already is. No grade guaranteed."
        path="/comic-services"
        jsonLd={[
          serviceSchema({ name: "Comic book cleaning, rehabilitation and preservation", description: "Evaluation, careful rehabilitation and preservation-minded handling for comic books, led by a trained collector's eye.", path: "/comic-services" }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Comic Science", path: "/comic-services" }]),
        ]}
      />

      <PageHead
        eyebrow="Comic Science"
        title="COMIC SCIENCE"
        sub="Decades of collectible experience. A trained eye for books worth saving."
        tone="blue"
      />

      {/* MAIN STORY */}
      <Wrap className="py-16">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 items-start">
          <Prose>
            <P className="text-lg text-[color:var(--chrome-1)]">
              Kit Allen has spent decades around collectible paper — from stamps and trading cards to
              vintage and modern comic books.
            </P>
            <P>
              Over the years, Kit has had the opportunity to examine, handle and work around some
              extraordinary and historically significant comics.
            </P>
            <P className="text-[color:var(--chrome-1)]">That experience has built something that cannot be purchased in a bottle:</P>
            <ul className="space-y-2.5 border-l-2 border-[color:var(--cyan)]/40 pl-5">
              {EYE_POINTS.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[color:var(--chrome-1)]">
                  <Eye size={16} className="text-[color:var(--cyan)] shrink-0 mt-1" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <P>
              At CardScience HQ, comic care is not about forcing a book to look new. It is about helping
              an appropriate comic present as the best version of what it already is.
            </P>
          </Prose>

          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl pointer-events-none opacity-60"
              style={{ background: "radial-gradient(60% 60% at 50% 30%, rgba(59,123,255,0.30), rgba(155,92,255,0.15) 55%, transparent 75%)" }} />
            <div className="relative rounded-2xl overflow-hidden border border-[color:var(--line)] shadow-2xl aspect-[4/5] bg-black">
              <img src={ASSETS.comicAvengers4} alt="Avengers #4 (CGC 9.8) — a book Kit helped restore"
                className="w-full h-full object-cover" style={{ objectPosition: "center 66%" }} />
              <div className="absolute top-[3%] right-[4%] w-[36%] h-[7%] rounded-sm backdrop-blur-md bg-black/10" />
            </div>
            <p className="relative text-center text-[11px] font-tech tracking-widest text-[color:var(--chrome-3)] mt-3">
              RESTORATION KIT WAS PART OF &middot; NOT OWNED BY CARDSCIENCE HQ
            </p>
          </div>
        </div>
      </Wrap>

      {/* SIGNATURE LINE */}
      <section className="relative overflow-hidden border-y border-[color:var(--line)] py-16 noise">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(70% 100% at 50% 50%, rgba(155,92,255,0.18), transparent 65%)" }} />
        <div className="relative mx-auto max-w-[900px] px-5 text-center">
          <p className="h-display text-2xl sm:text-4xl leading-tight text-chrome">
            A GREAT COMIC DOESN&apos;T ALWAYS NEED RESTORATION.
            <span className="block text-holo mt-2">SOMETIMES IT JUST NEEDS THE RIGHT EYE.</span>
          </p>
        </div>
      </section>

      {/* KIT ALLEN */}
      <Wrap className="py-16">
        <div className="grid md:grid-cols-[1fr_1.3fr] gap-10 items-start">
          <div>
            <Label>Kit Allen</Label>
            <H2>KIT ALLEN</H2>
            <p className="text-sm font-tech tracking-widest text-[color:var(--chrome-2)] mt-2">
              COLLECTOR. PRESERVATIONIST. COMIC EYE.
            </p>
          </div>
          <Prose>
            <P>
              Kit has been part of the collectible world for more than six decades of combined family and
              professional experience spanning comic books, stamps, sports cards and trading cards.
            </P>
            <P>
              His experience with significant comics has given him a strong eye for identifying books that
              may respond well to careful rehabilitation and, just as importantly, recognizing books that
              should not be disturbed.
            </P>
            <Callout holo>
              <Impact tone="holo">We do not believe every comic should be worked on.</Impact>
              <P className="mt-2 text-[color:var(--chrome-1)]">We believe every comic should first be understood.</P>
            </Callout>
          </Prose>
        </div>
      </Wrap>

      {/* GALLERY — REAL COMICS. REAL EXPERIENCE. */}
      <section className="py-16 border-t border-[color:var(--line)]">
        <div className="mx-auto max-w-[1400px] px-5">
          <div className="text-center mb-12">
            <Label>Real Comics. Real Experience.</Label>
            <H2 className="mx-auto">BOOKS THAT BUILT THE EYE</H2>
            <p className="text-[color:var(--chrome-3)] text-sm mt-3 max-w-xl mx-auto">
              Books Kit was part of restoring — work he's genuinely proud of. Shown to illustrate the craft; none are owned by CardScience HQ, and every certification number has been redacted.
            </p>
          </div>

          <div className="space-y-6">
            {BOOKS.map((b, i) => (
              <div key={b.title}
                className={`grid md:grid-cols-2 gap-6 lg:gap-10 items-center rounded-3xl border border-[color:var(--line)] glass overflow-hidden ${i % 2 ? "md:[direction:rtl]" : ""}`}>
                {/* full image */}
                <div className="relative [direction:ltr] bg-black">
                  <div className="absolute inset-0 pointer-events-none opacity-50"
                    style={{ background: "radial-gradient(60% 60% at 50% 40%, rgba(59,123,255,0.22), transparent 70%)" }} />
                  {b.cropLabel ? (
                    <div className="relative aspect-[4/5] overflow-hidden">
                      <img src={b.img} alt={`${b.title} — ${b.meta}`} data-testid={`comic-full-${i}`}
                        className="w-full h-full object-cover" style={{ objectPosition: "center 66%" }} />
                      <div className="absolute top-[3%] right-[4%] w-[36%] h-[7%] rounded-sm backdrop-blur-md bg-black/10" />
                    </div>
                  ) : (
                    <img src={b.img} alt={`${b.title} — ${b.meta}`} data-testid={`comic-full-${i}`}
                      className="relative w-full h-full object-contain max-h-[560px] p-6" />
                  )}
                </div>
                {/* detail + caption */}
                <div className="[direction:ltr] p-7 lg:p-10">
                  <p className="eyebrow text-[color:var(--violet)] mb-3">{b.tag}</p>
                  <h3 className="h-display text-2xl sm:text-3xl text-chrome">{b.title}</h3>
                  <p className="text-sm font-tech tracking-widest text-[color:var(--cyan)] mt-2">{b.meta}</p>
                  <p className="text-[color:var(--chrome-2)] leading-relaxed mt-5">{b.note}</p>
                  {/* close-up detail crop */}
                  <div className="mt-6 rounded-xl overflow-hidden border border-[color:var(--line)] h-28 sm:h-36 bg-black">
                    <img src={b.img} alt={`${b.title} cover detail`} data-testid={`comic-detail-${i}`}
                      className="w-full h-full object-cover" style={{ objectPosition: b.pos }} />
                  </div>
                  <p className="text-[11px] text-[color:var(--chrome-3)] mt-3 font-tech tracking-widest">RESTORATION KIT WAS PART OF &middot; NOT OWNED BY CARDSCIENCE HQ</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMIC REHABILITATION */}
      <Wrap className="py-16">
        <Label>Comic Rehabilitation</Label>
        <H2>REHAB THE BOOK.<span className="block text-holo">DON&apos;T REWRITE ITS HISTORY.</span></H2>
        <P className="mt-5">
          Depending on the comic, age, paper, inks and condition, CardScience HQ may evaluate opportunities to:
        </P>
        <div className="mt-6">
          <CheckList items={REHAB} />
        </div>
        <div className="mt-8">
          <Disclaimer>
            <p>No result is guaranteed.</p>
            <p>Some defects are permanent.</p>
            <p>Some books should not be treated at all.</p>
          </Disclaimer>
        </div>
      </Wrap>

      {/* WHAT WE WILL NOT DO */}
      <Wrap className="py-6">
        <Callout>
          <Label>What We Will Not Do</Label>
          <div className="mt-2">
            <DontList items={WONT} />
          </div>
        </Callout>
      </Wrap>

      {/* TRUST STATEMENT */}
      <Wrap className="py-16">
        <div className="rounded-3xl border border-[color:var(--line)] glass p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-4">
            <Lock size={20} className="text-[color:var(--cyan)]" />
            <p className="eyebrow">Trust Statement</p>
          </div>
          <Prose>
            <P className="text-[color:var(--chrome-1)]">
              We have been fortunate to be around important books. That does not give us permission to use
              someone else&apos;s collection as advertising.
            </P>
            <P>
              Every comic and every client remains private by default. Any customer-owned comic shown
              publicly must have explicit permission.
            </P>
          </Prose>
        </div>
      </Wrap>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-[color:var(--line)] py-20 noise">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(60% 100% at 50% 0%, rgba(59,123,255,0.22), transparent 60%)" }} />
        <div className="relative mx-auto max-w-[760px] px-5 text-center">
          <BookOpen size={28} className="text-[color:var(--cyan)] mx-auto mb-5" />
          <h2 className="h-display text-3xl sm:text-4xl text-chrome">HAVE A COMIC WORTH SAVING?</h2>
          <p className="text-[color:var(--chrome-2)] mt-5 max-w-lg mx-auto leading-relaxed">
            Send us clear photos before you ship anything. We&apos;ll tell you whether we believe the book
            is a candidate for evaluation.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-9">
            <Link to="/submit" data-testid="submit-comic-btn"
              className="btn-holo rounded-xl px-8 py-4 text-sm inline-flex items-center justify-center gap-2">
              SUBMIT A COMIC <ArrowRight size={16} />
            </Link>
            <Link to="/contact" data-testid="comic-evaluation-btn"
              className="btn-ghost rounded-xl px-8 py-4 text-sm inline-flex items-center justify-center gap-2">
              <ShieldCheck size={16} /> GET A COMIC EVALUATION
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
